import { Stack } from "@fluentui/react";
import { Container } from "../../Common/Container/Container";
import { Chatbot } from "../../Chatbot/Chatbot";
import { actionParentContainerStyles, actionsContainerStyles, buttonRowStyles, chatContainerStyles, chatInnerContainerStyles, containerStyles, leftContainerStyles, mdActionsParentContainerStyles, onlyActionsContainerStyles, onlyLeftContainerStyles, profileBoxContainerStyles, rightContainerStyles, smAtionsParentContainerStyles } from "./Styles/CustomerDashboardStyles";
import { Breakpoint, BreakpointCalculationSign, BreakpointType, GetBreakpoint } from "../../Common/Container/ContainerFluid";
import MediaQuery from "react-responsive";
import { useContext, useEffect, useRef, useState } from "react";
import { useBreakpoint, useBreakpointCalculation, useScreenViewportByBreakpoint } from "../../Common/Container/ContainerStyles";
import { CustomerInformationContext, ICustomerInformationContext } from "../../../ApplicationCode/Customer/CustomerInformationContext";
import { ISessionStateContext, SessionStateContext } from "../../../Core/State/SessionStateContext";
import { ProfileBox } from "../../Common/Dashboard/DashboardComponents";
import { DashboardButton } from "./Components/DashboardButtons";
import { Row } from "../../Common/Container/Grid/Row";
import { Col, col12 } from "../../Common/Container/Grid/Col";
import { useNavigate } from "react-router-dom";
import { useApplicationContext } from "../../Common/ApplicationContainer/ApplicationContainer";
import { SystemDataLoadingStatus, SystemOperation } from "../../../Model/SystemModels";
import { SystemCore } from "../../../Core/System/SystemCore";
import { Loader } from "../../Common/Loader/Loader";
import { CustomerService } from "../../../ApplicationCode/Customer/CustomerService";
import { DashboardLink, DashboardLinkImportance, DashboardLinkType } from "../../../Model/CommonModel";
import { MessageType, ToastService } from "../../../Core/Toast/ToastService";
import { ApplicationForUserType, Customer } from "../../../Model/CustomerModels";
import { t } from "i18next";
import { CustomerBillingInfoCard } from "../CustomerBillingInfoCard/CustomerBillingInfoCard";
import { PageContainerBase } from "../../Common/Template/PageContainer";
import { CommonFunctions } from "../../../ApplicationCode/Common/CommonFunctions";
import { CustomerBookingModal } from "./Components/BookingModal";

import bcIcon from '../../../Assets/Images/BusinessCentral_scalable.svg';
import bcIconDisabled from '../../../Assets/Images/BusinessCentral_DISABLED.svg';
import { ProvisioningInProgress } from "./Components/ProvisioningInProgress";
import { SwitchToProduction } from "./Components/SwitchToProduction";
import { RegisterService } from "../../Common/Register/Service/RegisterService";
import { HttpHelper } from "../../../Core/Http/HttpHelper";

export const CustomerDashboard: React.FC = (props) => {
    const currBreakpointType = useScreenViewportByBreakpoint();
    const isMD = useBreakpoint(BreakpointType.md);
    const isSM = useBreakpoint(BreakpointType.sm);
    const [breakpointxl] = useState<Breakpoint>(GetBreakpoint(BreakpointType.xl));
    const onlyLeftContainer = useBreakpointCalculation(BreakpointCalculationSign.lessThanOrEqual, currBreakpointType, breakpointxl.maxWidth);

    const [dataStatus, setDataStatus] = useState<SystemDataLoadingStatus>(SystemDataLoadingStatus.ToLoad);
    const [importanceVisible, setImportanceVisible] = useState<DashboardLinkImportance>(DashboardLinkImportance.Normal);
    const [dashboardLinks, setDashboardLinks] = useState<DashboardLink[]>([]);
    const [normalLinks, setNormalLinks] = useState<JSX.Element[]>([]);
    const [additionalLinks, setAdditionalLinks] = useState<JSX.Element[]>([]);
    const [shopLink, setShopLink] = useState<string>("");
    const [purchaseClicked, setPurchasedClicked] = useState<boolean>(false);
    const [showBookingModal, setShowBookingModal] = useState<boolean>(false);
    const billingCardRef = useRef<PageContainerBase>(null);

    const sessionContext: ISessionStateContext = useContext<ISessionStateContext>(SessionStateContext);
    const customerInfoContext: ICustomerInformationContext = useContext<ICustomerInformationContext>(CustomerInformationContext);
    const applicationContext = useApplicationContext();
    const navigate = useNavigate();
    
    const customerService = new CustomerService();

    //#region DASHBOARD LINKS

    const initializeData = () => {
        setDataStatus(SystemDataLoadingStatus.Loading);

        customerService.getDashboardLink().then((response: DashboardLink[]) => {
            setDashboardLinks(response);
            buildDashboardLinks(response);

            setDataStatus(SystemDataLoadingStatus.Loaded);
        })
        .catch((error) => {
            ToastService.showMessage(MessageType.Error, CustomerService.translate("dashboardLinkError"));
        });
    }

    const buildDashboardLinks = (dashboardLinks: DashboardLink[]) => {
        let groupedLinks = new Map<string, DashboardLink[]>();

        //Add promoted links
        groupedLinks = buildPromotedDashboardLinks();
        
        //Group links by group code
        dashboardLinks.forEach(link => {
            if (!groupedLinks.has(link.groupCode)) {
                groupedLinks.set(link.groupCode, []);
            }

            groupedLinks.get(link.groupCode)!.push(link);
        });

        //Build normal and additional links
        let normalLinks: JSX.Element[] = [];
        let additionalLinks: JSX.Element[] = [];
        groupedLinks.forEach((links, groupCode) => {
            if (groupCode === "ADMINISTRATION") {
                normalLinks.push(
                    <Row 
                        key={groupCode}
                    >
                        <Col 
                            colClass={col12}
                        >
                            <DashboardButton
                                highlight={false}
                                enabled={sessionContext.currentUser.mainUser && sessionContext.currentUser.isAad}
                                icon="Admin"
                                title={CustomerService.translate("dashboardLink:administrationTitle")}
                                subtitle={CustomerService.translate("dashboardLink:administrationSubtitle")}
                                onClick={() => {
                                    handleLinkClick(DashboardLinkImportance.Additional);
                                }}
                            />
                        </Col>
                    </Row>
                );

                additionalLinks = [...additionalLinks, ...buildLinkRows(links, groupCode)];
            }
            else {
                normalLinks.push(
                    <Stack>
                        {buildLinkRows(links, groupCode)}
                    </Stack>
                );
            }
        });

        //Add back button
        if(additionalLinks.length > 0) {
            additionalLinks.push(buildBackDashboardLink());
        }

        //Set links
        setNormalLinks(normalLinks);
        setAdditionalLinks(additionalLinks);
    };

    const buildLinkRows = (links: DashboardLink[], groupCode: string): JSX.Element[] => {
        let cols: JSX.Element[] = [];
        let linkRows: JSX.Element[] = [];
        links.forEach((link, index) => {
            const isSingle = links.length === 1;
            const isLastOdd = (index === links.length - 1) && (links.length % 2 !== 0);

            cols.push(
                <Col 
                    key={link.code}
                    colClass={isSingle || isLastOdd ? col12 : ""}
                >
                    <DashboardButton
                        highlight={link.hightlight}
                        enabled={isLinkEnabled(link)}
                        icon={link.iconName}
                        img={groupCode === "BC_ENVIRONMENT" ? link.enabled ? bcIcon : bcIconDisabled : undefined}
                        title={link.translatedDescription}
                        subtitle={link.translatedToolTipText}
                        onClick={() => { 
                            handleLinkClick(DashboardLinkImportance.Normal, link) 
                        }}
                        onRenderRightElement={() => {
                            switch(link.code) {
                                case 'PRODUCTION':
                                    if(isSwitchToProductionVisible(link))
                                        return (
                                            <SwitchToProduction
                                                customer={customerInfoContext.customer}
                                                handleRedirectToShop={handleRedirectToShop}
                                                onSwitchSuccess={() => initializeData()}
                                            />
                                        );
                                    
                                    return <></>
                                default:
                                    return <></>
                            }
                        }}
                    />
                </Col>
            );

            if ((index + 1) % 2 === 0 || index === links.length - 1) {
                linkRows.push(
                    <Row 
                        key={`${groupCode}-${index}`}
                        styles={buttonRowStyles}
                    >
                        {cols}
                    </Row>
                );
                
                cols = [];
            }
        });

        return linkRows;
    }

    const buildPromotedDashboardLinks = ():  Map<string, DashboardLink[]> => {
        let groupedLinks = new Map<string, DashboardLink[]>();
        if((!customerInfoContext.customer || !customerInfoContext.customer.trialWizardCompleted)) {
            groupedLinks.set("TRIAL", [
                {
                    active: true,
                    code: "TRIAL",
                    description: CustomerService.translate("dashboardLink:trialTitle"),
                    translatedDescription: CustomerService.translate("dashboardLink:trialTitle"),
                    toolTipText: CustomerService.translate("dashboardLink:trialDescription"),
                    translatedToolTipText: CustomerService.translate("dashboardLink:trialDescription"),
                    enabled: sessionContext.currentUser.mainUser,
                    iconName: 'AccountActivity',
                    forUserType: ApplicationForUserType.AdminUser,
                    groupCode: 'TRIAL',
                    groupDescription: 'TRIAL',
                    translatedGroupDescription: 'TRIAL',
                    linkType: DashboardLinkType.InternalLink,
                    order: 0,
                    linkUrl: '/app/register',
                    hightlight: sessionContext.currentUser.mainUser
                }
            ]);
        }

        return groupedLinks;
    }

    const buildBackDashboardLink = (): JSX.Element => {
        return (
            <Row
                key={"backRow"}
            >
                <Col 
                    key="back"
                    colClass={col12}
                >
                    <DashboardButton
                        enabled
                        icon="Back"
                        title={CustomerService.translate("dashboardLink:backButtonTitle")}
                        subtitle={CustomerService.translate("dashboardLink:backButtonSubtitle")}
                        onClick={() => { 
                            setImportanceVisible(DashboardLinkImportance.Normal);
                        }}
                    />
                </Col>
            </Row>
        );
    }

    const isLinkEnabled = (link: DashboardLink): boolean => {
        let enabled: boolean = link.enabled;

        if(link.forUserType === ApplicationForUserType.AdminUser && !(link.code === "SHOP"))
            enabled = sessionContext.currentUser.mainUser;
        
        return enabled;
    }

    const isSwitchToProductionVisible = (link: DashboardLink): boolean => {
        const dsStatus: string[] = ["30", "50", "60", "70", "80"];
        if(!link.enabled && sessionContext.currentUser.isAad && dsStatus.includes(customerInfoContext.customer.statusCode))
            return true;

        return false;
    }

    const handleLinkClick = (importance: DashboardLinkImportance, link?: DashboardLink) => {
        switch (importance) {
            case DashboardLinkImportance.Normal:
                switch (link?.linkType) {
                    case DashboardLinkType.InternalLink:
                        if(link.code === 'HELP')
                            navigate(link.linkUrl + "/" + sessionContext.currentApp.culture);
                        else
                            navigate(link.linkUrl);
                        break;
                    case DashboardLinkType.ExternalLink:
                        if (link.code === 'SHOP')
                            handleRedirectToShop();
                        else
                            window.open(link.linkUrl, '_blank');
                        break;
                    case DashboardLinkType.OpenPage:
                        switch(link.linkUrl)
                        {
                            case 'bookingAvailability':
                                setShowBookingModal(true);
                                break;
                            case 'invoicingData':
                                customerInfoContext.reloadCustomer(customerInfoContext.customer.id).then((customer) => {
                                    billingCardRef.current!.setRecord(customer);
                                    billingCardRef.current!.openPage(SystemOperation.Update);
                                });
                                break;
                        }
                        break;
                    case DashboardLinkType.MailTo:
                        window.open(link.linkUrl);
                        break;
                }
                break;
            case DashboardLinkImportance.Additional:
                setImportanceVisible(DashboardLinkImportance.Additional);
                break;
        }
    }

    //#endregion DASHBOARD LINKS

    //#region UTILITY

    const handleRedirectToShop = () => {
        if(!sessionContext.currentUser.isAad){
            ToastService.showMessage(MessageType.Error, CustomerService.translate("buyShortcutErr"));
            return;
        }

        ToastService.showMessage(MessageType.Pending, t("leadDashboard:messages:text00002msg")!, 'handleRedirectToShop');
        customerInfoContext.reloadCustomer(customerInfoContext.customer.id).then((customer) => {
            customerService.generateShopLink().then((link: string) => {
                setShopLink(link);
                if(checkCustomerBillingData(customer)){
                    ToastService.update("handleRedirectToShop", { isLoading: false, render: () => t('customerCenter:Text00001Lbl')!, type: "info"});
                    window.open(link,'_self');
                }
                else {
                    setPurchasedClicked(true);
                    billingCardRef.current!.setRecord(customer);
                    billingCardRef.current!.openPage(SystemOperation.Update);
                    ToastService.update('handleRedirectToShop', { isLoading: false, render: () => t('customerCenter:Err00003Text')!, type: "warning"});
                }
            })
            .catch(() => {
                ToastService.update('handleRedirectToShop', { isLoading: false, render: () => t("leadDashboard:errors:text00005Err")!, type: "error"});
            })
        })
        .catch(() => {
            ToastService.update('handleRedirectToShop', { isLoading: false, render: () => t("leadDashboard:errors:text00005Err")!, type: "error"});
        })
    }
        
    const checkCustomerBillingData = (current: Customer): boolean => {
        let customerData: Object = {};
        
        switch (current.legalEntityCode) {
            case 'ESS':
                customerData = {
                    companyName: current.companyName,
                    firstName: current.firstName,
                    lastName: current.lastName,
                    phoneNo: current.phoneNo,
                    streetAddress: current.streetAddress,
                    zipCode: current.zipCode,
                    city: current.city,
                    county: current.county,
                    country: current.country,
                    email: current.email,
                    legalRepresentativeFirstName: current.legalRepresentativeFirstName,
                    legalRepresentativeLastName: current.legalRepresentativeLastName,
                };
                break;
            default:
                customerData = {
                    companyName: current.companyName,
                    firstName: current.firstName,
                    lastName: current.lastName,
                    phoneNo: current.phoneNo,
                    streetAddress: current.streetAddress,
                    zipCode: current.zipCode,
                    city: current.city,
                    county: current.county,
                    country: current.country,
                    vat: current.vat,
                    sdi: current.sdi,
                    pecEmail: current.pecEmail,
                    email: current.email,
                    legalRepresentativeFirstName: current.legalRepresentativeFirstName,
                    legalRepresentativeLastName: current.legalRepresentativeLastName,
                };
                break;
        };
                
        let statusValue = true;
        const customerDataKeys: string[] = Object.keys(customerData);
        for(let I = 0; I < customerDataKeys.length - 1; I++) {
            const currentValue: string = Object.values(customerData)[I];
            
            if (!currentValue) {
                statusValue = false;
                break;
            }
        }
        
        return statusValue;
    };
    
    const reloadCustomerBillingInfoCardData = (operationType: SystemOperation, rec: Customer)=>{
        if (!checkCustomerBillingData(rec)){
            customerInfoContext.reloadCustomer(rec.id).then(() => {
                ToastService.showMessage(MessageType.Error, t('customerCenter:Err00004Text'));
                billingCardRef.current?.handleError();
            })
            .catch((jqXHR) => {
                ToastService.showMessage(MessageType.Error, jqXHR.message);
                billingCardRef.current?.handleError();
            });
        }
        else
        {
            if (!(rec.legalEntityCode === 'ESS')) 
            {
                CommonFunctions.validateVat(rec.vat)
                .then((resp) => {
                    if (resp.risultato === "NON_VALIDA" || resp.risultato === "ERROR")
                    {
                        customerInfoContext.reloadCustomer(rec.id).then(() => {
                            ToastService.showMessage(MessageType.Warning, t('customerCenter:Err00005Text')+": "+rec.vat);
                            billingCardRef.current?.handleError();
                        })
                        .catch((jqXHR) => {
                            ToastService.showMessage(MessageType.Error, jqXHR.message);
                            billingCardRef.current?.handleError();
                        });
                    }
                    else
                        updateCustomer(rec);
                })
                .catch((err) => {
                    customerInfoContext.reloadCustomer(rec.id).then(() => {
                        ToastService.showMessage(MessageType.Error, err.message);
                        billingCardRef.current?.handleError();
                    })
                    .catch((jqXHR) => {
                        ToastService.showMessage(MessageType.Error, jqXHR.message);
                        billingCardRef.current?.handleError();
                    });
                })
            }
            else
                updateCustomer(rec);
        }
    }
    
    const updateCustomer = (rec: Customer) => {
        customerInfoContext.updateCustomer(rec).then(() => {
            customerInfoContext.reloadCustomer(rec.id).then(() => {
                billingCardRef.current?.closePage();
                ToastService.showMessage(MessageType.Success, t('customerCenter:Text00002Lbl'));
                
                if (purchaseClicked)
                {
                    window.open(shopLink,'_blank');
                    setPurchasedClicked(false);
                }
            })
            .catch((jqXHR) => {
                ToastService.showMessage(MessageType.Error, jqXHR.message);
                billingCardRef.current?.handleError();
            });
        })
        .catch((jqXHR) => {
            customerInfoContext.reloadCustomer(rec.id).then(() => {
                ToastService.showMessage(MessageType.Error, jqXHR.message);
                billingCardRef.current?.handleError();
            });
        });
    }

    const isProvisioningInProgress = (): boolean => {
        if(!customerInfoContext.customer)
            return false;
        if(!customerInfoContext.customer.trialWizardCompleted)
            return false;
        if(customerInfoContext.customer.provisioningCompleted)
            return false;

        return true;
    };

    const handleShortcut = () => {
        const shortcut = HttpHelper.getParameter("shortCutBuy");
        if(shortcut) {
            handleRedirectToShop();
        }
    };

    //#endregion UTILITY

    useEffect(() => {
        applicationContext.setFullScreen(false);
        initializeData();
    }, []);

    useEffect(() => {
        if(SystemCore.isFinishedDataStatus(dataStatus)) {
            handleShortcut();
        }
    }, [dataStatus])

    if(!SystemCore.isFinishedDataStatus(dataStatus))
        return (
            <Loader 
                text={CustomerService.translate("messageContent1")}
            />
        );

    return (
        <Container>
            <Stack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="start"
                verticalFill
                className={containerStyles}
            >
                <Stack
                    className={onlyLeftContainer ? onlyLeftContainerStyles : leftContainerStyles}
                >
                    <Stack
                        horizontal
                        horizontalAlign="center"
                        className={profileBoxContainerStyles}
                    >
                        <ProfileBox 
                            user={sessionContext.currentUser}
                        />
                    </Stack>
                    <Stack
                        verticalFill
                        verticalAlign="end"
                        className={
                            isSM ?
                                smAtionsParentContainerStyles :
                            isMD ?
                                mdActionsParentContainerStyles 
                            :
                                actionParentContainerStyles
                        }
                    >
                        <Stack
                            verticalFill
                            verticalAlign="space-between"
                            className={
                                isProvisioningInProgress() ?
                                    actionsContainerStyles
                                :
                                    onlyActionsContainerStyles
                            }
                        >
                            {
                                isProvisioningInProgress() &&
                                    <Stack
                                        horizontal
                                        horizontalAlign="center"
                                        verticalAlign="end"
                                    >
                                        <ProvisioningInProgress />
                                    </Stack>
                            }
                            {
                                importanceVisible === DashboardLinkImportance.Normal ? 
                                    normalLinks 
                                : 
                                    additionalLinks
                            }
                        </Stack>
                    </Stack>
                    <CustomerBookingModal
                        showModal={showBookingModal}
                        onDismiss={()=>{
                            setShowBookingModal(false);
                        }}
                    />
                    <CustomerBillingInfoCard 
                        onSuccess={reloadCustomerBillingInfoCardData} 
                        ref={billingCardRef} 
                        record={customerInfoContext.customer ? customerInfoContext.customer : new Customer()} 
                    />
                </Stack>
                <MediaQuery
                    minWidth={breakpointxl.minWidth}
                >
                    <Stack
                        className={rightContainerStyles}
                    >
                        <Chatbot
                            displayAppNavbar
                            hideChatbotNavbar
                            chatContainerStyles={chatContainerStyles}
                            chatInnerContainerStyles={chatInnerContainerStyles}
                            embedded
                        />
                    </Stack>
                </MediaQuery>
            </Stack>
        </Container>
    );
}